import {
  Avatar,
  Box,
  ButtonBase,
  ButtonBaseProps,
  Stack,
  Typography,
} from '@mui/material'
import { Icons, ItemImageArea } from 'utoniq-ui'

import { Item, ItemType } from '@/clients/utoniq-core/schema'
import { ItemIcon } from '@/components/organisms'
import { getEmphasisRaritySx } from '@/components/pages/GachaResult/template/TenGachaResult/LightFrame'
import { styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { Organism } from '@/core/architecture'

export type ItemImageProps = ButtonBaseProps & {
  item: Partial<Item> & {
    isUserProtected?: boolean
    isSystemProtected?: boolean
    isPrinted?: boolean
  }
  ownedCount?: number
  noItem?: boolean
  href?: string
  showIcon?: boolean
  showShadow?: boolean
  onClick?: () => void
  component?: string
}

export const ItemImage: Organism<ItemImageProps> = ({
  item,
  noItem,
  ownedCount,
  showShadow,
  showIcon = false,
  ...props
}) => {
  const { t } = useTranslation('userOwnedItems')

  return (
    <ButtonBase
      {...props}
      sx={{ flexDirection: 'column', width: '100%', position: 'relative' }}
    >
      {showIcon && (
        <Stack
          sx={{
            zIndex: 1,
            marginInline: 'auto',
            pl: 1,
            width: 104,
            position: 'absolute',
            top: 8,
          }}
        >
          {(item?.isDownloadable || item.metadata?.wallpaper?.isPrintEnabled) &&
          item.type === ItemType.Wallpaper ? (
            <Stack
              direction="row"
              spacing={'2px'}
              sx={{
                bgcolor: 'grey.50',
                borderRadius: 100,
                height: 24,
                width:
                  item?.isDownloadable &&
                  item.metadata?.wallpaper?.isPrintEnabled
                    ? 40
                    : 24,
                p: 1,
              }}
              alignItems="center"
              justifyContent="center"
            >
              {item?.isDownloadable && (
                <Icons.WallpaperDownload
                  sx={{ color: 'grey.700', width: '14px', height: '14px' }}
                />
              )}
              {item.metadata?.wallpaper?.isPrintEnabled && (
                <Icons.WallpaperPrinter
                  sx={{ color: 'grey.700', width: '14px', height: '14px' }}
                />
              )}
            </Stack>
          ) : (
            <ItemIcon type={item.type} size="small" />
          )}
        </Stack>
      )}
      <Box
        sx={
          !noItem && showShadow
            ? {
                position: 'absolute',
                top: 0,
                width: 104,
                height: 166,
                ...getEmphasisRaritySx(item.rarity?.id, 12),
              }
            : undefined
        }
      />
      {item?.isPrinted && (
        <Stack
          sx={{
            position: 'absolute',
            top: 0,
            width: 104,
            height: 166,
            zIndex: 1,
            bgcolor: 'Background',
            opacity: 0.9,
            justifyContent: 'center',
          }}
        >
          <Typography variant="overline" color="text.secondary">
            {t('printed', 'プリント済')}
          </Typography>
        </Stack>
      )}
      <ItemImageArea
        noItem={noItem}
        src={item.thumbnail?.front?.mediumUri ?? ''}
      />
      <Stack
        sx={{ pt: 2, width: '100%', height: '100%', minHeight: '48px' }}
        spacing={1}
        alignItems="flex-start"
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          flex={1}
          sx={{ minWidth: 0, width: '100%' }}
        >
          <Typography
            variant="overline"
            sx={{
              ...styleSets.lineCamp(2),
              wordBreak: 'break-all',
            }}
            align="left"
          >
            {item.title}
          </Typography>

          {item.rarityIconImage?.smallUri && (
            <Avatar
              variant="square"
              src={item.rarityIconImage.smallUri}
              sx={{
                height: 12,
                width: 12,
                mx: 'auto',
              }}
            />
          )}
        </Stack>
        {!!item.sku && (
          <Typography
            variant="overline"
            sx={{
              ...styleSets.lineCamp(3),
              wordBreak: 'break-all',
              width: '100%',
            }}
            align="left"
          >
            {item.sku.unitName}
          </Typography>
        )}
        <Stack direction="row" spacing={1}>
          <Icons.OwnedItems fontSize="xSmall" sx={{ color: 'grey.600' }} />
          <Typography variant="overline">{ownedCount ?? 0}</Typography>
        </Stack>
      </Stack>
      {showIcon && (
        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            gap: '2px',
            position: 'absolute',
            width: 104,
            marginInline: 'auto',
            bottom: 52,
            zIndex: 1,
            pr: 1,
          }}
        >
          {item.isResaleAvailable && (
            <Icons.Resale fontSize="small" filledColor />
          )}
          {(item.isUserProtected || item.isSystemProtected) && (
            <Icons.Protect fontSize="small" filledColor />
          )}
          {!!item?.nft?.chains && <Icons.Nft fontSize="small" filledColor />}
        </Stack>
      )}
    </ButtonBase>
  )
}
